// App
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';

// Actions
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import { DEFAULT } from 'app/shared/models/Filter';
import { filterUtils_getFilterPillLabels } from 'app/shared/utils/filterUtils';
import { yieldCallback } from '@zillow/yield-callback';

export const useMobileLogic = () => {
  // Redux
  const dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction> = useDispatch();
  const filter = useSelector((state: RootReduxState) => state.filter);
  const { resetFilter } = useFilterChange();

  // React
  const [filterLabelCounts, setFilterLabelCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const filterLabels = filterUtils_getFilterPillLabels(filter);

  const fetchNumListingsExcludedPerFilter = async () => {
    const filterLabelsAndCounts: Record<string, string> = {};
    /**
     * For each filter pill label, fetch the number of listings that would be present IF
     * the filter was not active!
     *
     * How this works:
     * Take the entire current filter as is, grab the default value for the current
     * filter label in question, and send that off to byCoords to "see" how many
     * properties we're excluding.
     */

    await Promise.all(
      filterLabels.map(async (labelTuple) => {
        if (!labelTuple) return;
        const key = labelTuple[0];
        const filterWithKeyReset = Object.assign({}, filter, { [key]: DEFAULT[key as keyof typeof DEFAULT] });
        setIsLoading(true);
        const numOfListings = await dispatch(
          ListingEngineActions.fetchListingsByCoordsForFilterPills({ filter: filterWithKeyReset }),
        );

        filterLabelsAndCounts[key] = String(numOfListings);
      }),
    );
    setFilterLabelCounts(filterLabelsAndCounts);
    setIsLoading(false);
  };

  const fetchTotalNumListingsExcluded = async () => {
    await dispatch(ListingEngineActions.fetchExcludedListingsByCoords());
    setIsLoading(false);
  };

  const initialize = useCallback(() => {
    // Get listings excluded per filter
    fetchNumListingsExcludedPerFilter();

    // Get total listings excluded from all filters
    fetchTotalNumListingsExcluded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearAllFilters = () => {
    resetFilter();
  };

  const _yieldFilterItemRemoval = yieldCallback((key: string) => {
    resetFilter({ [key]: DEFAULT[key as keyof typeof DEFAULT] });
  });

  const handleFilterItemRemoval = (key: string) => {
    _yieldFilterItemRemoval(key);
  };

  return {
    initialize,
    handleClearAllFilters,
    handleFilterItemRemoval,
    filterLabels,
    filterLabelCounts,
    isLoading,
  };
};
